import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CountdownBadge } from "../components/CountdownBadge";
import { IconAlert, IconExchange, IconShield, IconWallet } from "../components/icons";
import { Tabs } from "../components/Tabs";
import { HeroAmount } from "../components/motion";
import { ChainBadge, Notice, Skeleton } from "../components/ui";
import { useBrand } from "../branding/BrandContext";
import { allowedModes } from "../branding/config";
import { useInvoice } from "../hooks/useInvoice";
import { useNow } from "../hooks/useNow";
import { useI18n } from "../i18n";
import { computeCountdown } from "../lib/countdown";
import { monogram } from "../lib/format";
import { formatUnits } from "../lib/money";
import { checkoutView, isFinal } from "../lib/status";
import type { PayMode, PublicInvoice } from "../lib/types";
import { WalletPay } from "../wallet/WalletPay";
import { ExchangePay } from "./pay/ExchangePay";
import { ProgressView } from "./pay/ProgressView";
import { ResultView } from "./pay/ResultView";
import { ErrorState } from "./ErrorState";
import { PageShell } from "./PageShell";

const MockControls = import.meta.env.DEV && import.meta.env.VITE_MOCK === "1" ? lazy(() => import("../mock/MockControls")) : null;

export function PayPage() {
  const { id = "" } = useParams();
  const { invoice, loading, error, transport, reload } = useInvoice(id);

  if (loading && !invoice) return <PayLoading />;
  if (!invoice) return <ErrorState code={error ?? "network"} onRetry={() => void reload()} />;
  return (
    <>
      <Checkout invoice={invoice} transport={transport} />
      {MockControls ? (
        <Suspense fallback={null}>
          <MockControls invoiceId={invoice.id} />
        </Suspense>
      ) : null}
    </>
  );
}

function Checkout({ invoice, transport }: { invoice: PublicInvoice; transport: string }) {
  const { t } = useI18n();
  const final = isFinal(invoice.status);
  const now = useNow(1000, !final);
  const view = checkoutView(invoice, now);
  const countdown = computeCountdown(invoice.createdAt, invoice.expiresAt, now);
  const brand = useBrand();
  const modes = useMemo<PayMode[]>(
    () => allowedModes(invoice.modes.length ? invoice.modes : ["wallet"], brand),
    [invoice.modes, brand],
  );
  // Auto-return to the store only if this page saw the payment arrive, not on a later revisit.
  const [sawOpen] = useState(() => !isFinal(invoice.status));
  const [mode, setMode] = useState<PayMode>(modes[0] ?? "wallet");
  const [submittedTx, setSubmittedTx] = useState<string | null>(null);

  useEffect(() => {
    if (!modes.includes(mode)) setMode(modes[0] ?? "wallet");
  }, [modes, mode]);

  useEffect(() => {
    document.title = `${t("pay.payTo", { merchant: invoice.merchantName })} · ${t("app.title")}`;
  }, [invoice.merchantName, t]);

  const showProgress = view === "confirming" || (view === "pay" && submittedTx !== null);

  let panel;
  if (view === "pay" && !showProgress && modes.length === 0) {
    panel = (
      <Notice tone="info" title={t("pay.noMethodsTitle")}>
        {t("pay.noMethodsBody")}
      </Notice>
    );
  } else if (view === "pay" && !showProgress) {
    const partial = invoice.status === "partially_paid" && invoice.receivedAmount ? invoice : null;
    panel = (
      <>
        {partial ? (
          <Notice tone="warning" title={t("pay.partialTitle")}>
            {t("pay.partialBody", {
              received: formatUnits(partial.receivedAmount!, invoice.asset.decimals),
              amount: formatUnits(invoice.amount, invoice.asset.decimals),
              missing: formatUnits((BigInt(invoice.amount) - BigInt(partial.receivedAmount!) > 0n ? BigInt(invoice.amount) - BigInt(partial.receivedAmount!) : 0n).toString(), invoice.asset.decimals),
              asset: invoice.asset.symbol,
            })}
          </Notice>
        ) : null}
        {modes.length > 1 ? (
          <Tabs<PayMode>
            idBase="pay"
            label={t("pay.methodLabel")}
            active={mode}
            onChange={setMode}
            tabs={modes.map((m) =>
              m === "wallet"
                ? { key: m, label: t("pay.tabWallet"), icon: <IconWallet size={17} /> }
                : { key: m, label: t("pay.tabExchange"), icon: <IconExchange size={17} /> },
            )}
          />
        ) : (
          <h2 className="section-title">{mode === "wallet" ? t("pay.tabWallet") : t("pay.tabExchange")}</h2>
        )}
        <div
          className="tab-panel"
          role={modes.length > 1 ? "tabpanel" : undefined}
          id={`pay-panel-${mode}`}
          aria-labelledby={modes.length > 1 ? `pay-tab-${mode}` : undefined}
        >
          {mode === "wallet" ? (
            <WalletPay
              invoice={invoice}
              onSubmitted={setSubmittedTx}
              onUseExchange={modes.includes("exchange") ? () => setMode("exchange") : undefined}
            />
          ) : (
            <ExchangePay invoice={invoice} walletAvailable={modes.includes("wallet")} />
          )}
        </div>
      </>
    );
  } else if (showProgress) {
    panel = <ProgressView invoice={invoice} submittedTx={submittedTx} />;
  } else {
    panel = (
      <ResultView invoice={invoice} view={view as "paid" | "overpaid" | "underpaid" | "expired" | "flagged"} sawPayment={sawOpen} />
    );
  }

  return (
    <PageShell
      invoiceId={invoice.id}
      printTitle={final ? t("print.title") : undefined}
      banner={
        invoice.demo ? (
          <div className="demo-banner" role="note">
            <IconAlert size={15} />
            <strong>{t("pay.demoBanner")}</strong>
          </div>
        ) : undefined
      }
    >
      <div className="checkout">
        <Summary invoice={invoice} countdown={countdown} showCountdown={view === "pay"} transport={final ? null : transport} />
        <main className={`pay-panel panel${showProgress ? " is-progress" : ""}${final ? " is-final" : ""}`} id="main">
          {panel}
        </main>
      </div>
      {final ? null : (
      <div className="mobile-foot">
        <p className="trust">
          <IconShield size={16} />
          <span>{t("common.nonCustodial", { merchant: invoice.merchantName })}</span>
        </p>
        <Link className="link small" to={`/i/${encodeURIComponent(invoice.id)}`}>
          {t("pay.statusPage")}
        </Link>
      </div>
      )}
    </PageShell>
  );
}

function Summary({
  invoice,
  countdown,
  showCountdown,
  transport,
}: {
  invoice: PublicInvoice;
  countdown: ReturnType<typeof computeCountdown>;
  showCountdown: boolean;
  transport: string | null;
}) {
  const { t } = useI18n();
  return (
    <aside className="summary panel" aria-label={t("pay.amountDue")}>
      <div className="merchant">
        <span className="monogram" aria-hidden="true">
          {monogram(invoice.merchantName)}
        </span>
        <span className="merchant-name">{invoice.merchantName}</span>
        {transport ? (
          <span className={`live-dot${transport === "live" ? " is-live" : ""}`} title={transport === "live" ? t("pay.live") : t("pay.reconnecting")}>
            <span className="sr-only">{transport === "live" ? t("pay.live") : t("pay.reconnecting")}</span>
          </span>
        ) : null}
      </div>

      <div className="due">
        <div className="due-label">{isFinal(invoice.status) ? t("pay.total") : t("pay.amountDue")}</div>
        <HeroAmount value={formatUnits(invoice.amount, invoice.asset.decimals)} symbol={invoice.asset.symbol} animate={false} />
        {invoice.description ? <p className="due-description">{invoice.description}</p> : null}
      </div>

      <dl className="summary-meta">
        <div>
          <dt>{t("pay.network")}</dt>
          <dd>
            <ChainBadge chain={invoice.asset.chain} />
          </dd>
        </div>
        {showCountdown ? (
          <div>
            <dt>{countdown.urgent ? t("pay.expiresSoon") : t("pay.expiresIn")}</dt>
            <dd>
              <CountdownBadge countdown={countdown} />
            </dd>
          </div>
        ) : null}
        {invoice.orderId ? (
          <div className="summary-meta-order">
            <dt>{t("pay.order")}</dt>
            <dd className="num">#{invoice.orderId}</dd>
          </div>
        ) : null}
      </dl>

      <div className="summary-foot">
        <p className="trust">
          <IconShield size={16} />
          <span>{t("common.nonCustodial", { merchant: invoice.merchantName })}</span>
        </p>
        <Link className="link small" to={`/i/${encodeURIComponent(invoice.id)}`}>
          {t("pay.statusPage")}
        </Link>
      </div>
    </aside>
  );
}

function PayLoading() {
  const { t } = useI18n();
  return (
    <PageShell>
      <div className="checkout" aria-busy="true">
        <aside className="summary panel">
          <div className="merchant">
            <Skeleton width={36} height={36} radius={10} />
            <Skeleton width={120} height={16} />
          </div>
          <div className="due">
            <Skeleton width={90} height={12} />
            <Skeleton width={220} height={56} radius={12} />
            <Skeleton width={160} height={14} />
          </div>
        </aside>
        <main className="pay-panel panel">
          <Skeleton height={46} radius={12} />
          <div style={{ height: 20 }} />
          <Skeleton height={64} radius={12} />
          <div style={{ height: 12 }} />
          <Skeleton height={64} radius={12} />
          <span className="sr-only" role="status">
            {t("common.loading")}
          </span>
        </main>
      </div>
    </PageShell>
  );
}
